import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Brain, User, Mail, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { authService } from '../../services/authService';
import { useAuthStore } from '../../store/authStore';

const registerSchema = z.object({
  username: z.string().min(3, 'Mínimo 3 caracteres').max(50, 'Máximo 50 caracteres'),
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'Mínimo 6 caracteres'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Las contraseñas no coinciden',
  path: ['confirmPassword'],
});

type RegisterForm = z.infer<typeof registerSchema>;

export default function RegisterPage() {
  const navigate = useNavigate();
  const { setUser } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
  });
  
  const onSubmit = async (data: RegisterForm) => {
    try {
      setIsLoading(true);
      const result = await authService.register({
        username: data.username,
        email: data.email,
        password: data.password,
      });
      setUser(result.user, result.profile);
      toast.success('¡Cuenta creada exitosamente!');
      navigate('/juegos');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al registrarse');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="w-full max-w-md glass-container">
      <div className="text-center mb-8">
        <Brain className="w-16 h-16 text-neureka-yellow mx-auto mb-4" />
        <h1 className="text-3xl font-display font-bold mb-2">Crear cuenta</h1>
        <p className="text-white/70">Comienza tu entrenamiento mental</p>
      </div>
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            <User className="w-4 h-4 inline mr-2" />
            Usuario
          </label>
          <input
            {...register('username')}
            type="text"
            className="input-neureka"
            placeholder="tunombre"
            disabled={isLoading}
          />
          {errors.username && (
            <p className="text-red-400 text-sm mt-1">{errors.username.message}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">
            <Mail className="w-4 h-4 inline mr-2" />
            Email
          </label>
          <input
            {...register('email')}
            type="email"
            className="input-neureka"
            placeholder="tu@email.com"
            disabled={isLoading}
          />
          {errors.email && (
            <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">
            <Lock className="w-4 h-4 inline mr-2" />
            Contraseña
          </label>
          <input
            {...register('password')}
            type="password"
            className="input-neureka"
            placeholder="••••••••"
            disabled={isLoading}
          />
          {errors.password && (
            <p className="text-red-400 text-sm mt-1">{errors.password.message}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">
            <Lock className="w-4 h-4 inline mr-2" />
            Confirmar contraseña
          </label>
          <input
            {...register('confirmPassword')}
            type="password"
            className="input-neureka"
            placeholder="••••••••"
            disabled={isLoading}
          />
          {errors.confirmPassword && (
            <p className="text-red-400 text-sm mt-1">{errors.confirmPassword.message}</p>
          )}
        </div>
        
        <button
          type="submit"
          disabled={isLoading}
          className="btn-neureka w-full flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Creando cuenta...
            </>
          ) : (
            'Crear cuenta gratis'
          )}
        </button>
      </form>
      
      <div className="mt-6 text-center">
        <p className="text-white/70">
          ¿Ya tienes cuenta?{' '}
          <Link to="/login" className="text-neureka-yellow font-semibold hover:underline">
            Inicia sesión
          </Link>
        </p>
      </div>
    </div>
  );
}
